import { cn } from "@/utils/cn";
import * as React from "react";
import { memo } from "react";

/**
 * Input — shadcn primitive migrated to class-selector + co-located CSS.
 *
 * REFERENCE PATTERN: class-selector + co-located CSS (see shadcn-authoring.md
 * commit b35c0a615). Fifth execution of the shadcn-authoring template
 * after Button / Card / Badge / Skeleton.
 *
 * Public API preserved EXACTLY: React.ComponentProps<"input"> spread on
 * the wrapped <input>. forwardRef + displayName preserved (was already
 * present in pre-migration source). No cva, no inputVariants export.
 *
 * Size: `data-size="lg"` (the 48px field, the Button's lg) is passed as the
 * attribute itself, since `size` is the native input attribute.
 *
 * Role: `data-role="display"` sets the page-title type, for a page title typed
 * in place (usually with `variant="ghost"`). Also passed as the attribute
 * itself: `role` is the ARIA attribute.
 *
 * One axis, mirroring Textarea's: `variant`.
 *   default — the field: its own border, fill, radius and focus ring.
 *   ghost   — the seam: no chrome at all, because the container around it
 *             IS the field. Written as a `:where()` reset at (0,1,0), so a
 *             surface skin can still dress over it — a chip you type in
 *             keeps its own radius and inset. A search bar with a magnifier and an input, a
 *             chip with an editable label, a composer row — four agent-builder
 *             sheets each hand-wrote this same reset as a descendant rule
 *             (`.dw-set-state__search input`, `.dw-dropdown-panel__search
 *             input`, `.dw-enum-chips .dw-chip input`,
 *             `.dw-agent-preview-canvas__composer input`) because the atom
 *             had no word for it. It has one now.
 *
 * Source: docs/implementation-blueprint.md §8.3 (class-selector pattern);
 *         .claude/agent-prompts/shadcn-authoring.md (binding template, commit b35c0a615);
 *         .claude/agent-prompts/atom-authoring-class-selector.md (shared rules);
 *         src/components/ui/shadcn/textarea.tsx (the same axis, same spelling).
 */
interface InputProps extends React.ComponentProps<"input"> {
    /** Visual treatment. @default "default" */
    variant?: "default" | "ghost";
}

const Input = memo(
    React.forwardRef<HTMLInputElement, InputProps>(({ className, type, variant = "default", ...props }, ref) => (
        // eslint-disable-next-line no-restricted-syntax -- This IS the base Input component; must use raw HTML input
        (<input ref={ref} type={type} className={cn("dw-input", className)} data-variant={variant} data-component="Input" data-location="src/components/ui/shadcn/input.tsx" {...props} />)
    ))
);
Input.displayName = "Input";

export { Input };
